import { NgZone } from '@angular/core';
import { detect } from 'detect-browser';
import { Dimensions } from 'src/app/core/types';
import { OrthographicCamera, PerspectiveCamera, Vector3 } from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { CameraBoxManager } from './camera-box/camera-box-manager';
import { DEFAULT_DIRECTIONAL_VECTOR } from './utils/constants';
import { LoadingSpace } from './loading-space';
import RenderingContext from './rendering-context';
import { SharedProjectService } from '../../core/services/shared-project.service';
import { MainView } from './main-view';

export interface OrthographicCameraManager {
  viewFromTopSide(): void;
  viewFromLeftSide(): void;
  viewFromRightSide(): void;
}

export class DefaultPerspectiveCameraManager {
  public cameraBoxManager: CameraBoxManager;
  private isMobile: boolean;
  private isSharedView = false;

  constructor(
    private loadingSpace: LoadingSpace,
    private htmlElement: HTMLElement,
    private rendereringContext: RenderingContext<PerspectiveCamera>,
    private unit: string,
    private zone: NgZone,
    private sharedProjectService: SharedProjectService,
    private mainView: MainView
  ) {
    const browser = detect();
    this.isMobile =
      browser.os === 'iOS' ||
      browser.os === 'Android OS' ||
      browser.os === 'BlackBerry OS' ||
      browser.os === 'Windows Mobile' ||
      browser.os === 'Amazon OS';
    this.sharedProjectService.isViewingShared.subscribe(isShared=> {
      this.isSharedView = isShared
    });
    this.addControls();
    this.setCameraTargetToCenterOfLoadingSpaceBase();
    this.setDefaultCameraPosition();
    this.calculateZoom();
    this.rendereringContext.cameraControls.update();
    this.rendereringContext.camera.updateProjectionMatrix();
    this.cameraBoxManager = new CameraBoxManager(htmlElement, this.zone, this.mainView);
    this.cameraBoxManager.addEventListener(
      'cameraboxchange',
      (event: { type: string; object: Vector3 }) => this.sync(event),
    );
    this.syncCameraBoxWithVisualisation();

    window.addEventListener('resize', () => {
      this.onWindowResize();
      this.calculateZoom();
    });
  }

  public resetCamera(loadingSpace?: LoadingSpace, unit?: string): void {
    if (unit) {
      this.unit = unit;
    }
    if (loadingSpace) {
      this.loadingSpace = loadingSpace;
    }
    this.setCameraTargetToCenterOfLoadingSpaceBase();
    this.rendereringContext.camera.position
      .copy(this.rendereringContext.cameraControls.target)
      .add(DEFAULT_DIRECTIONAL_VECTOR.clone().multiplyScalar(180000));
    this.rendereringContext.cameraControls.update();
    this.calculateZoom();
    if (this.cameraBoxManager) {
      this.cameraBoxManager.syncCameraBox(
        this.rendereringContext.camera.position
          .clone()
          .sub(this.rendereringContext.cameraControls.target),
      );
    }
  }

  public disableCameraBox() {
    this.cameraBoxManager.cameraBoxRenderingContext.scene.visible = false;
  }

  public onWindowResize(): void {
    const horizontalChangeRatio =
      this.htmlElement.clientWidth / this.rendereringContext.renderer.domElement.clientWidth;
    this.rendereringContext.camera.zoom *= horizontalChangeRatio;
    this.rendereringContext.camera.aspect =
      this.htmlElement.clientWidth / this.htmlElement.clientHeight;
    this.rendereringContext.renderer.setSize(
      this.htmlElement.clientWidth,
      this.htmlElement.clientHeight,
    );
    this.rendereringContext.camera.updateProjectionMatrix();
  }

  public setCameraTargetToCenterOfLoadingSpaceBase(): void {
    this.rendereringContext.cameraControls.target.set(
      this.loadingSpace.width / 2,
      0,
      this.loadingSpace.getLoadingSpaceLength() / 2,
    );
  }

  public calculateZoom(): void {
    const lenghtOfVisibleGround =  this.unit === 'cm' || this.unit === 'mm' ? 100000 : 100000 / 2.5;
    const di = new Vector3();
    di.z = di.z + this.loadingSpace.getLoadingSpaceLength() / 2;
    const distance = di.distanceTo(this.rendereringContext.camera.position);
    const vFov = this.rendereringContext.camera.fov * (Math.PI / 180);
    const height = 2 * Math.tan(vFov / 2) * distance;
    const width = height * this.rendereringContext.camera.aspect;
    if (width <= this.htmlElement.clientWidth) {
      this.rendereringContext.camera.zoom =
        width / (this.loadingSpace.getLoadingSpaceLength() + lenghtOfVisibleGround);
    } else {
      this.rendereringContext.camera.zoom =
        (this.htmlElement.clientWidth /
          (this.loadingSpace.getLoadingSpaceLength() + lenghtOfVisibleGround)) *
        (width / this.htmlElement.clientWidth);
    }

    if (this.isMobile) {
      this.rendereringContext.camera.zoom *= 0.85;
    }

    if (this.isSharedView) {
      this.rendereringContext.camera.zoom *= 1.20;
    }

    this.rendereringContext.camera.updateProjectionMatrix();
    this.rendereringContext.cameraControls.maxDistance = distance * 3;
    this.rendereringContext.cameraControls.minDistance = distance / 3;
  }

  private setDefaultCameraPosition(): void {
    this.rendereringContext.camera.position
      .copy(this.rendereringContext.cameraControls.target)
      .add(DEFAULT_DIRECTIONAL_VECTOR.clone().multiplyScalar(180000));
  }

  private addControls(): void {
    this.zone.runOutsideAngular(() => {
      this.rendereringContext.cameraControls = new OrbitControls(
        this.rendereringContext.camera,
        this.rendereringContext.renderer.domElement,
      );
      this.rendereringContext.cameraControls.enablePan = false;
    });
  }

  private syncCameraBoxWithVisualisation() {
    let lol1 = false;
    this.rendereringContext.cameraControls.addEventListener('start', () => {
      lol1 = true;
    });
    this.rendereringContext.cameraControls.addEventListener('end', () => {
      lol1 = false;
    });

    this.rendereringContext.cameraControls.addEventListener('change', () => {
      if (lol1) {
        this.cameraBoxManager.syncCameraBox(
          this.rendereringContext.camera.position
            .clone()
            .sub(this.rendereringContext.cameraControls.target),
        );
      }
    });
  }

  private sync(event: { type: string; object: Vector3 }) {
    this.setCameraDirection(event.object);
  }

  private setCameraDirection(direction: Vector3) {
    const distance = this.rendereringContext.camera.position
      .clone()
      .sub(this.rendereringContext.cameraControls.target)
      .length();
    this.rendereringContext.camera.position
      .copy(this.rendereringContext.cameraControls.target)
      .add(direction.clone().normalize().multiplyScalar(distance));
    this.rendereringContext.cameraControls.update();
  }
}

export class PrintPerspectiveCameraManager {
  private loadingSpaceTopDimensions: Dimensions;

  constructor(
    private rendereringContext: RenderingContext<PerspectiveCamera>,
    public loadingSpace: LoadingSpace,
    public htmlElement: HTMLElement,
    private zone: NgZone,
  ) {
    this.rendereringContext.camera = new PerspectiveCamera(
      45,
      this.htmlElement.clientWidth / this.htmlElement.clientHeight,
      100,
      10000000,
    );

    this.loadingSpaceTopDimensions = {
      height:
        this.loadingSpace.height > this.loadingSpace.dimensionsOfCabin.height
          ? this.loadingSpace.height
          : this.loadingSpace.dimensionsOfCabin.height,
      width:
        this.loadingSpace.width > this.loadingSpace.dimensionsOfCabin.width
          ? this.loadingSpace.width
          : this.loadingSpace.dimensionsOfCabin.width,
      length: this.loadingSpace.getLoadingSpaceLengthWithCabinAndDoors() + this.loadingSpace.gap,
    };
    this.addControls();
    this.setCameraTargetToCenterOfLoadingSpace();
    this.setDefaultCameraPosition();
    this.fitLoadingSpaceInCanvas();
    this.rendereringContext.cameraControls.update();
    this.rendereringContext.camera.updateProjectionMatrix();
  }

  public setCameraTargetToCenterOfLoadingSpace() {
    this.rendereringContext.cameraControls.target.set(
      this.loadingSpaceTopDimensions.width / 2,
      this.loadingSpaceTopDimensions.height / 2,
      this.loadingSpace.getLoadingSpaceLength() / 2,
    );
  }

  public setDefaultCamera() {
    this.setCameraTargetToCenterOfLoadingSpace();
    this.setDefaultCameraPosition();
    this.rendereringContext.cameraControls.update();
    this.rendereringContext.camera.updateProjectionMatrix();
  }

  private setDefaultCameraPosition() {
    this.rendereringContext.camera.position
      .copy(this.rendereringContext.cameraControls.target)
      .add(DEFAULT_DIRECTIONAL_VECTOR.clone().multiplyScalar(180000));
  }

  private addControls() {
    this.zone.runOutsideAngular(() => {
      this.rendereringContext.cameraControls = new OrbitControls(
        this.rendereringContext.camera,
        this.htmlElement,
      );
    });
  }

  private fitLoadingSpaceInCanvas() {
    const horizontalZoom = this.calculateHorizontalZoom();
    const verticalZoom = this.calculateVerticalZoom();
    const maxZoom = Math.min(horizontalZoom, verticalZoom);
    this.rendereringContext.camera.zoom = maxZoom;
  }

  private calculateHorizontalZoom() {
    const height = this.calculateVisibleHeightInCamera();
    const width = height * this.rendereringContext.camera.aspect;
    let zoom: number;
    const distanceToEdge = 6000;
    if (width <= this.htmlElement.clientWidth) {
      zoom = width / (this.loadingSpaceTopDimensions.length + distanceToEdge);
    } else {
      zoom =
        (this.htmlElement.clientWidth / (this.loadingSpaceTopDimensions.length + distanceToEdge)) *
        (width / this.htmlElement.clientWidth);
    }
    return zoom;
  }

  private calculateVerticalZoom() {
    const height = this.calculateVisibleHeightInCamera();
    let zoom: number;
    const distanceToEdge = 15000;
    if (height <= this.htmlElement.clientHeight) {
      zoom = height / (this.loadingSpaceTopDimensions.height + distanceToEdge);
    } else {
      zoom =
        (this.htmlElement.clientHeight / (this.loadingSpaceTopDimensions.height + distanceToEdge)) *
        (height / this.htmlElement.clientHeight);
    }
    return zoom;
  }

  private calculateVisibleHeightInCamera(): number {
    const positionOfLoadingSpaceElement = new Vector3();
    positionOfLoadingSpaceElement.sub(this.rendereringContext.cameraControls.target);
    positionOfLoadingSpaceElement.z =
      positionOfLoadingSpaceElement.z + this.loadingSpaceTopDimensions.length / 2;
    positionOfLoadingSpaceElement.y =
      positionOfLoadingSpaceElement.y + this.loadingSpaceTopDimensions.height;
    positionOfLoadingSpaceElement.x =
      positionOfLoadingSpaceElement.x - this.loadingSpaceTopDimensions.width;
    const distance = positionOfLoadingSpaceElement.distanceTo(
      this.rendereringContext.camera.position,
    );

    const vFov = this.rendereringContext.camera.fov * (Math.PI / 180);
    return 2 * Math.tan(vFov / 2) * distance;
  }
}

export class OrthographicCameraManager implements OrthographicCameraManager {
  private loadingSpaceTopDimensions: Dimensions;

  constructor(
    private rendereringContext: RenderingContext<OrthographicCamera>,
    private loadingSpace: LoadingSpace,
    private htmlElement: HTMLElement,
    private zone: NgZone,
  ) {
    this.loadingSpaceTopDimensions = {
      height:
        this.loadingSpace.height > this.loadingSpace.dimensionsOfCabin.height
          ? this.loadingSpace.height
          : this.loadingSpace.dimensionsOfCabin.height,
      width:
        this.loadingSpace.width > this.loadingSpace.dimensionsOfCabin.width
          ? this.loadingSpace.width
          : this.loadingSpace.dimensionsOfCabin.width,
      length: this.loadingSpace.getLoadingSpaceLengthWithCabinAndDoors() + this.loadingSpace.gap,
    };

    this.rendereringContext.camera = new OrthographicCamera(
      this.loadingSpaceTopDimensions.length / -2,
      this.loadingSpaceTopDimensions.length / 2,
      this.loadingSpaceTopDimensions.height / 2,
      this.loadingSpaceTopDimensions.height / -2,
      -1000000,
      1000000,
    );

    this.addControls();
    this.setCameraTargetToCenterOfLoadingSpace();
    this.viewFromTopSide();
    this.rendereringContext.cameraControls.update();
  }

  public setCameraTargetToCenterOfLoadingSpace() {
    this.rendereringContext.cameraControls.target.set(
      this.loadingSpace.width / 2,
      this.loadingSpaceTopDimensions.height / 2,
      this.loadingSpace.getLoadingSpaceLength() / 2,
    );
  }

  public viewFromLeftSide() {
    this.viewProjection({
      azimuth: -Math.PI / 2,
      polarAngle: Math.PI / 2,
    });
    if (
      this.htmlElement.clientWidth / this.htmlElement.clientHeight >
      this.loadingSpaceTopDimensions.length / this.loadingSpaceTopDimensions.height
    ) {
      this.rendereringContext.camera.left =
        (this.loadingSpaceTopDimensions.height *
          (this.htmlElement.clientWidth / this.htmlElement.clientHeight)) /
        -2;
      this.rendereringContext.camera.right =
        (this.loadingSpaceTopDimensions.height *
          (this.htmlElement.clientWidth / this.htmlElement.clientHeight)) /
        2;
      this.rendereringContext.camera.top = this.loadingSpaceTopDimensions.height / 2;
      this.rendereringContext.camera.bottom = this.loadingSpaceTopDimensions.height / -2;
    } else {
      this.rendereringContext.camera.left = this.loadingSpaceTopDimensions.length / -2;
      this.rendereringContext.camera.right = this.loadingSpaceTopDimensions.length / 2;
      this.rendereringContext.camera.top =
        this.loadingSpaceTopDimensions.length /
        (this.htmlElement.clientWidth / this.htmlElement.clientHeight) /
        2;
      this.rendereringContext.camera.bottom =
        this.loadingSpaceTopDimensions.length /
        (this.htmlElement.clientWidth / this.htmlElement.clientHeight) /
        -2;
    }
    this.rendereringContext.camera.updateProjectionMatrix();
  }

  public viewFromRightSide() {
    this.viewProjection({
      azimuth: Math.PI / 2,
      polarAngle: Math.PI / 2,
    });
    if (
      this.htmlElement.clientWidth / this.htmlElement.clientHeight >
      this.loadingSpaceTopDimensions.length / this.loadingSpaceTopDimensions.height
    ) {
      this.rendereringContext.camera.left =
        (this.loadingSpaceTopDimensions.height *
          (this.htmlElement.clientWidth / this.htmlElement.clientHeight)) /
        -2;
      this.rendereringContext.camera.right =
        (this.loadingSpaceTopDimensions.height *
          (this.htmlElement.clientWidth / this.htmlElement.clientHeight)) /
        2;
      this.rendereringContext.camera.top = this.loadingSpaceTopDimensions.height / 2;
      this.rendereringContext.camera.bottom = this.loadingSpaceTopDimensions.height / -2;
    } else {
      this.rendereringContext.camera.left = this.loadingSpaceTopDimensions.length / -2;
      this.rendereringContext.camera.right = this.loadingSpaceTopDimensions.length / 2;
      this.rendereringContext.camera.top =
        this.loadingSpaceTopDimensions.length /
        (this.htmlElement.clientWidth / this.htmlElement.clientHeight) /
        2;
      this.rendereringContext.camera.bottom =
        this.loadingSpaceTopDimensions.length /
        (this.htmlElement.clientWidth / this.htmlElement.clientHeight) /
        -2;
    }
    this.rendereringContext.camera.updateProjectionMatrix();
  }

  public viewFromTopSide() {
    this.viewProjection({
      azimuth: -Math.PI / 2,
      polarAngle: -Math.PI / 2,
    });
    if (
      this.htmlElement.clientWidth / this.htmlElement.clientHeight >
      this.loadingSpaceTopDimensions.length / this.loadingSpaceTopDimensions.width
    ) {
      this.rendereringContext.camera.left =
        (this.loadingSpaceTopDimensions.width *
          (this.htmlElement.clientWidth / this.htmlElement.clientHeight)) /
        -2;
      this.rendereringContext.camera.right =
        (this.loadingSpaceTopDimensions.width *
          (this.htmlElement.clientWidth / this.htmlElement.clientHeight)) /
        2;
      this.rendereringContext.camera.top = this.loadingSpaceTopDimensions.width / 2;
      this.rendereringContext.camera.bottom = this.loadingSpaceTopDimensions.width / -2;
    } else {
      this.rendereringContext.camera.left = this.loadingSpaceTopDimensions.length / -2;
      this.rendereringContext.camera.right = this.loadingSpaceTopDimensions.length / 2;
      this.rendereringContext.camera.top =
        this.loadingSpaceTopDimensions.length /
        (this.htmlElement.clientWidth / this.htmlElement.clientHeight) /
        2;
      this.rendereringContext.camera.bottom =
        this.loadingSpaceTopDimensions.length /
        (this.htmlElement.clientWidth / this.htmlElement.clientHeight) /
        -2;
    }
    this.rendereringContext.camera.updateProjectionMatrix();
  }

  private addControls() {
    this.zone.runOutsideAngular(() => {
      this.rendereringContext.cameraControls = new OrbitControls(
        this.rendereringContext.camera,
        this.htmlElement,
      );
    });
  }

  private viewProjection(projection: ProjectionSettings): void {
    this.rendereringContext.cameraControls.minAzimuthAngle = projection.azimuth;
    this.rendereringContext.cameraControls.maxAzimuthAngle = projection.azimuth + 0.001;
    this.rendereringContext.cameraControls.minPolarAngle = projection.polarAngle;
    this.rendereringContext.cameraControls.maxPolarAngle = projection.polarAngle;
    this.rendereringContext.cameraControls.update();
  }
}

interface ProjectionSettings {
  azimuth: number;
  polarAngle: number;
}
